import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { useSnackbar } from "notistack";
import GenericContainer from "../../../components/containers/GenericContainer";
import useShopContext from "../../../hooks/useShopContext";
import { getCustomDomains, createCustomDomain, deleteCustomDomain } from "../api";
import { SHOP_DOMAIN } from "../../../config";

const CustomDomain = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { shopfront, loading: shopContextLoading } = useShopContext();
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const fetchDomains = () => {
    if (shopfront?.vendor?.id && shopfront?.shopfront_id) {
      setIsFetching(true);
      getCustomDomains(shopfront.vendor.id, shopfront.shopfront_id)
        .then((res) => {
          setDomains(res.data || []);
        })
        .catch((err) => {
          console.error("Error fetching domains:", err);
          enqueueSnackbar(`Error fetching domains: ${err?.response?.data || err.message}`, {
            variant: "error",
          });
          setDomains([]);
        })
        .finally(() => {
          setIsFetching(false);
        });
    } else {
      setDomains([]);
    }
  };

  useEffect(() => {
    if (!shopContextLoading && shopfront?.shopfront_id) {
      fetchDomains();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopContextLoading, shopfront?.shopfront_id]);

  const handleInputChange = (e) => {
    setNewDomain(e.target.value);
    setError("");
  };

  const validateDomain = (domainToValidate) => {
    if (!domainToValidate) return false;
    const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/;
    return domainRegex.test(domainToValidate);
  };

  const handleAddDomain = () => {
    if (!validateDomain(newDomain)) {
      setError("Invalid domain format");
      return;
    }

    setIsLoading(true);
    createCustomDomain(shopfront.vendor.id, shopfront.shopfront_id, newDomain)
      .then((res) => {
        enqueueSnackbar("Custom domain added successfully!", { variant: "success" });
        setDomains([...domains, res.data]);
        setNewDomain("");
        setError("");
      })
      .catch((err) => {
        enqueueSnackbar(
          `Error: ${err.response?.data?.detail || err.response?.data || "Unknown error"}`,
          {
            variant: "error",
          },
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteDomain = (custom_domain_id) => {
    setIsLoading(true);
    deleteCustomDomain(shopfront.vendor.id, shopfront.shopfront_id, custom_domain_id)
      .then(() => {
        enqueueSnackbar("Custom domain deleted successfully!", { variant: "success" });
        setDomains(domains.filter((d) => d.id !== custom_domain_id));
      })
      .catch((err) => {
        enqueueSnackbar(
          `Error: ${err.response?.data?.detail || err.response?.data || "Unknown error"}`,
          {
            variant: "error",
          },
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <GenericContainer>
      <Typography variant="h5" gutterBottom>
        Custom Domains
      </Typography>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={12}>
          <TextField
            label="Add New Custom Domain"
            variant="outlined"
            fullWidth
            margin="normal"
            value={newDomain}
            onChange={handleInputChange}
            error={!!error}
            helperText={error || "Enter the domain name (e.g., shop.example.com)"}
            disabled={isLoading || isFetching}
          />
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Do not enter http:// or any trailing slashes.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddDomain}
            disabled={isLoading || isFetching || !newDomain || !validateDomain(newDomain)}
          >
            Add Domain
          </Button>
        </Grid>

        <Grid item xs={12} md={12}>
          <Typography variant="h6" gutterBottom>
            Configured Domains
          </Typography>
          {isFetching ? (
            <CircularProgress />
          ) : domains.length > 0 ? (
            <List dense>
              {domains.map((d) => (
                <ListItem key={d.id} divider>
                  <ListItemText
                    primary={d.domain}
                    secondary={
                      d.verified ? (
                        "Verified"
                      ) : (
                        <>
                          Verification pending. Add a CNAME record pointing to{" "}
                          <strong>cname.{SHOP_DOMAIN}</strong> in your DNS settings. Verification
                          can take some time. If the record is set, no further action is needed
                          here.
                        </>
                      )
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteDomain(d.id)}
                      disabled={isLoading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No custom domains configured yet.
            </Typography>
          )}
        </Grid>
        {(isLoading || isFetching) && (
          <Grid item xs={12} style={{ textAlign: "center", marginTop: "20px" }}>
            <CircularProgress />
          </Grid>
        )}
      </Grid>
    </GenericContainer>
  );
};

export default CustomDomain;
